import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { Button, TextField, Typography, Alert } from "@mui/material";
import { Stack } from "@mui/system";
import CheckIcon from "@mui/icons-material/Check";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Avatar } from "@mui/material";
import DataStatusAlert from "../../../../Health/DataAccuracy/UI_HelpersOf_DataAccuracy/DataStatusAlert_Helpers";
import { useSelector } from "react-redux";
import { selectUserToken, setDisplayErrorSnackBar } from "../../../../../../features/userSlice";
import { BusinessInsightsEndPointLogic } from "../../../../../../logic/AssetManagement/BusinessInsights/BusinessInsightsEndPointLogic";
import store from "../../../../../../store";
import SimpleBackdrop from "../../../../../BackDrop/BackDrop";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function ResolveInsightPopup({ open, onClose }: Props) {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const [comment, setComment] = React.useState<string>("");
  const taskDetails = useSelector((state: any) => state.businessInsights.taskDetails);
  const { selectedSearchSite } = useSelector((state: any) => state?.businessInsights);
  const [loading, setLoading] = React.useState<boolean>(false);
  const dispatch = useDispatch();
  const [searchParams,] = useSearchParams();
  const insightType = searchParams.get("insightType");

  const handleCloseResolveModal = () => { // Close the Resolve modal and clear the comment
    onClose(); // Close modal if success
    setComment(""); // Clear comment
  }

  const resolveBodyActionItem = {
    "shared_uid": taskDetails.shared_uid,
    "current_status": "Done",
  };

  const handleActions = async () => {
    if (!comment) return; // Prevent action if assignee or comment is not set
    setLoading(true); // Set loading state to true
    try {
      if (oauthTokenHeader && resolveBodyActionItem) {
        await BusinessInsightsEndPointLogic.resolveBusinessInsightsStatus(oauthTokenHeader, resolveBodyActionItem, taskDetails?.shared_uid); // Resolve the business insight
        await BusinessInsightsEndPointLogic.postCommentBusinessInsightTask(oauthTokenHeader, { object_type: "tasks", comment }, taskDetails?.shared_uid); // Post the comment
        await BusinessInsightsEndPointLogic.getTaskDetails(oauthTokenHeader, taskDetails?.shared_uid); // Refresh task details
        await BusinessInsightsEndPointLogic.getAllBusinessInsights(selectedSearchSite, oauthTokenHeader, dispatch); // Refresh all business insights
      }
    } catch (error: any) {
      store.dispatch(setDisplayErrorSnackBar({ message: error.message || "An error occurred while resolving the insight.", severity: "error" }));
    } finally {
      setLoading(false); // Reset loading state
      handleCloseResolveModal(); // Close modal after action
      store.dispatch(setDisplayErrorSnackBar({ message: "Bussiness Insight has been successfully resolved.", type: 'success' }));
      setComment(""); // Clear comment after action
    }
  };

  // useeffect hook to reset comment when the modal opens
  React.useEffect(() => {
    if (open) {
      setComment(""); // Reset comment when modal opens
    }
  }, [open]);

  return (
    <React.Fragment>
      {loading ? (
        <SimpleBackdrop />
      ) : (
        <Dialog open={open} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description" maxWidth="md" fullWidth>
        <DialogTitle id="alert-dialog-title">{"Resolve Insight"}</DialogTitle>
        <DialogContent>
          <TableContainer sx={{ marginBottom: "16px" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ padding: "6px 0px" }}>
                    <Typography fontWeight={500}>Task Name</Typography>
                  </TableCell>
                  <TableCell sx={{ padding: "6px 0px" }}>
                    <Typography fontWeight={500}>Assignee</Typography>
                  </TableCell>
                  <TableCell sx={{ padding: "6px 0px" }}>
                    <Typography fontWeight={500}>Status</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {taskDetails && (
                  <TableRow>
                    <TableCell sx={{ padding: "6px 0px" }}>
                      {taskDetails.data['query_output:sec_title'].value}
                    </TableCell>
                    <TableCell sx={{ padding: "6px 0px" }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar
                          alt={taskDetails.assignee_fullname}
                          src=""
                          sx={{ width: 24, height: 24 }}
                        />
                        <Typography variant="body2">
                          {taskDetails.assignee_fullname}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell padding="none">
                      <DataStatusAlert status={taskDetails.current_status} />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <DialogContentText id="alert-dialog-description">
            <Typography fontSize={14} fontWeight={400} color="#000000">
              Add Comment
            </Typography>
            <TextField
              sx={{ marginTop: "16px", "& .MuiFormHelperText-root": { marginLeft: 0, marginTop: 1, } }}
              id="outlined-multiline-static"
              label="Content"
              multiline
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              required
              rows={4}
              fullWidth
              placeholder="Explain why or how this task is resolved"
              color="secondary"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              helperText={"This field is mandatory"}
            />
            {taskDetails?.current_status === "Done" && (
              <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                This task is already resolved (Done).
              </Alert>
            )}
          </DialogContentText>
          <Stack direction="row" gap={1} justifyContent="flex-end">
            <Button variant="text" color="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              loading={loading}
              loadingPosition="end"
              data-tracker={insightType === "adgroup" ? "319" : "340"}
              disabled={!comment || loading || taskDetails?.current_status === "Done"}
              endIcon={loading && <CheckIcon fontSize="small" />}
              onClick={() => handleActions()}
            >
              Resolve
              <CheckIcon fontSize="small" />
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
      )}
    </React.Fragment>
  );
}
